/** Shared bits for the agency pages (messages, directory, court, fleet, permits, case files): paths that work in the office and the deputy app. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../shared/api.ts';
import { Icon } from '../shared/icons.tsx';

export const inField = () => location.pathname.startsWith('/field');
/** Path inside whichever app we're in: `/court/1` in the office, `/field/court/1` in the deputy app. */
export const at = (p: string) => (inField() ? `/field${p}` : p);

export const when = (iso: string | null | undefined, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) =>
  iso ? new Date(iso).toLocaleString('en-US', opts) : '—';
export const day = (iso: string | null | undefined) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
export const ago = (iso: string) => {
  const s = (Date.now() - +new Date(iso)) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400 && new Date(iso).toDateString() === new Date().toDateString()) return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Where a message's link goes. */
export function linkTo(l: { kind: string; id: string } | null | undefined): string | null {
  if (!l) return null;
  return { policy: at(`/policies/${l.id}`), court: at(`/court/${l.id}`), evidence: at(`/evidence/${l.id}`), casefile: at(`/case-files/${l.id}`), case: at(`/cases/${l.id}`), photos: at('/photos'), warrant: at(`/warrants/${l.id}`), 'warrant-request': at(`/warrants/requests/${l.id}`), training_submission: at('/training/review'), training: at('/training'), notice: at(`/pio/notices/${l.id}`), pio: at(`/pio/${l.id}`), tip: at(`/tips/${l.id}`), uof: at(`/uof/${l.id}`), personnel: at(`/personnel/${l.id}`), ews: at('/ews'), evaluation: at(`/evaluations/${l.id}`), fleet: at(`/fleet/${l.id}`), bolo: at(`/bolos/${l.id}`), applicant: at(`/applicants/${l.id}`), foia: at(`/pio/foia/${l.id}`), event: at(`/events/${l.id}`), safety: at(`/schools/safety/${l.id}`), bwc: at(`/bwc/${l.id}`), dailylog: at(`/daily-log/${l.id}`), ia: at(`/ia/${l.id}`), housecheck: at(`/house-checks/${l.id}`), command: at(`/command/${l.id}`) }[l.kind] ?? null;
}
export const LINK_LABEL: Record<string, string> = { policy: 'Open the policy', court: 'Open the court date', evidence: 'Open the evidence item', casefile: 'Open the case file', case: 'Open the case', photos: 'Review the photos', warrant: 'Open the warrant', 'warrant-request': 'Open the warrant request', training_submission: 'File the certificate', training: 'Open my training', notice: 'Open the notice', pio: 'Open it in PIO', tip: 'Open the tip', uof: 'Open the use of force report', personnel: 'Open it', ews: 'Open early warning', evaluation: 'Open the evaluation', fleet: 'Open the car', bolo: 'Open the BOLO', applicant: 'Open the applicant', foia: 'Open the FOIA request', event: 'Open the event', safety: 'Open the safety concern', bwc: 'Open the review', dailylog: 'Open the activity log', ia: 'Open the IA case', housecheck: 'Open the house check', command: 'Open the command board' };

/** Unread messages, refreshed every minute (and when a message is read). */
const listeners = new Set<() => void>();
export const refreshMailCount = () => listeners.forEach((f) => f());
export function useMailCount(enabled = true) {
  const [c, setC] = useState<{ unread: number; needResponse: number; overdue: number } | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () => api.get<any>('/api/mail/count').then((x) => alive && setC(x)).catch(() => undefined);
    load();
    const t = setInterval(load, 60_000);
    listeners.add(load);
    return () => {
      alive = false;
      clearInterval(t);
      listeners.delete(load);
    };
  }, [enabled]);
  return c;
}

export interface Person {
  id: string;
  name: string;
  title?: string | null;
  call_number?: string | null;
  unit?: string | null;
}

/** Pick several people: type to search, chips for who's picked. */
export function PeoplePicker({ people, value, onChange, label, placeholder = 'Type a name, call number or unit…' }: { people: Person[]; value: string[]; onChange: (v: string[]) => void; label: string; placeholder?: string }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLInputElement>(null);
  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    return people.filter((p) => !value.includes(p.id) && (!s || [p.name, p.call_number, p.unit, p.title].some((x) => x && x.toLowerCase().includes(s)))).slice(0, 8);
  }, [q, people, value]);
  const add = (id: string) => (onChange([...value, id]), setQ(''), setOpen(false), box.current?.focus());
  return (
    <div className="pp">
      <div className="pp-box" onClick={() => box.current?.focus()}>
        {value.map((id) => (
          <span key={id} className="pp-chip">
            {byId.get(id)?.name ?? 'Someone'}
            <button type="button" aria-label={`Remove ${byId.get(id)?.name}`} onClick={() => onChange(value.filter((x) => x !== id))}>
              ×
            </button>
          </span>
        ))}
        <input
          ref={box}
          aria-label={label}
          value={q}
          placeholder={value.length ? '' : placeholder}
          onChange={(e) => (setQ(e.target.value), setOpen(true))}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && hits[0]) (e.preventDefault(), add(hits[0].id));
            if (e.key === 'Backspace' && !q && value.length) onChange(value.slice(0, -1));
          }}
        />
      </div>
      {open && hits.length > 0 && (
        <ul className="pp-menu" role="listbox">
          {hits.map((p) => (
            <li key={p.id} role="option" aria-selected={false} onMouseDown={(e) => (e.preventDefault(), add(p.id))}>
              <strong>{p.name}</strong>
              <span className="muted small">{[p.call_number, p.title, p.unit].filter(Boolean).join(' · ')}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function KPI({ n, label, tone, onClick, on }: { n: number | string; label: string; tone?: 'warn' | 'bad' | 'ok'; onClick?: () => void; on?: boolean }) {
  const C = onClick ? 'button' : 'div';
  return (
    <C type={onClick ? 'button' : undefined} className={`ops-kpi ${tone ?? ''} ${on ? 'on' : ''}`} onClick={onClick}>
      <strong>{n}</strong>
      <span>{label}</span>
    </C>
  );
}

export function EmptyState({ icon, title, children }: { icon: string; title: string; children?: React.ReactNode }) {
  return (
    <div className="card empty-state">
      <Icon name={icon} size={26} />
      <strong>{title}</strong>
      {children && <span className="muted">{children}</span>}
    </div>
  );
}

/** Read a picked file as base64 (no data: prefix). */
export function fileBase64(f: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(',')[1] ?? '');
    r.onerror = () => rej(r.error);
    r.readAsDataURL(f);
  });
}
